import React from "react";
import Reveal from "./Reveal";
import Doodle from "./Doodle";

// Act 5 of the story site: the belonging.
//
// The emotional payload of the whole page, and the one section that is
// deliberately almost empty. Everything before it is arguing; this is the bit
// that just shows what it looks like when it has worked.
//
// Full-bleed coral, one line, one sticker breaking out of the container. The
// sticker OVERLAPS the section boundary on purpose: every other element on the
// page sits inside its box, so a single deliberate escape is what stops the
// layout reading as a stack of tidy rectangles.
//
// No headline claim, no statistic, no testimonial. We have neither the
// consented quotes nor the member numbers to put here honestly, and a made-up
// figure under a photograph of people is exactly the kind of thing that would
// have to be quietly removed later.
//
// COLOURS ARE PINNED, NOT TOKENS. `bg-salmon` is the brand accent and stays the
// same light coral in both themes, by design. Everything drawn ON it therefore
// has to be pinned too: `text-ink` flips to near-white in dark mode, which put
// this headline at 1.81:1 and the body copy at 1.56:1 against the coral, i.e.
// invisible, which is exactly how it shipped on 2026-07-31. DownloadAct already
// does this correctly on its teal ground. The rule: a fixed ground needs a
// fixed foreground.

const BelongingAct: React.FC = () => (
  <section className="relative w-full bg-salmon overflow-hidden">
    <Doodle
      name="starFilled"
      size={30}
      className="absolute w-8 h-8 md:w-auto md:h-auto left-[8%] top-[16%] text-[#f9f9f7]/70"
    />
    <Doodle
      name="squiggle"
      size={100}
      rotate={-8}
      className="absolute w-8 h-8 md:w-auto md:h-auto right-[10%] top-[12%] text-[#f9f9f7]/50"
    />
    <Doodle
      name="star"
      size={44}
      rotate={16}
      className="absolute w-8 h-8 md:w-auto md:h-auto left-[14%] bottom-[14%] text-on-salmon/25"
    />

    <div className="max-w-6xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-2 gap-10 md:gap-6 items-center">
      <div className="relative z-10">
        <Reveal y={22}>
          <p
            className="font-headline text-on-salmon tracking-[-0.03em] leading-[1.08] text-[clamp(2.2rem,6.4vw,4.2rem)] text-balance"
            style={{ fontWeight: 700 }}
          >
            One day you look up and these are just{" "}
            <span className="italic">your people.</span>
          </p>
        </Reveal>
        <Reveal y={14} delay={120}>
          <p className="mt-6 text-lg md:text-xl text-on-salmon/80 leading-relaxed max-w-md">
            Nobody plans a friendship group. You just keep turning up to small
            things, with the same faces, until one day it is not small any more.
          </p>
        </Reveal>
      </div>

      {/* Breaks the section edge: the only element on the page allowed to. */}
      <Reveal y={30} delay={100} className="relative md:-mr-10 lg:-mr-20">
        <img
          src="/story/embrace-sticker.webp"
          srcSet="/story/embrace-sticker-sm.webp 420w, /story/embrace-sticker.webp 760w"
          sizes="(max-width: 768px) 92vw, 46vw"
          alt="Friends with their arms around each other, laughing"
          loading="lazy"
          className="w-full h-auto -rotate-2 drop-shadow-[0_30px_50px_rgba(45,56,73,0.22)]"
        />
      </Reveal>
    </div>
  </section>
);

export default BelongingAct;
